import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { initSupabase, getSupabase } from "@/lib/supabase";

const AuthContext = createContext(null);
export const NEXT_KEY = "rz_after_login";

export const AuthProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [user, setUser] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const uidRef = useRef(null);

  const refreshOnboarding = useCallback(async () => {
    const { data } = await api.get("/onboarding");
    setOnboarding(data);
    return data;
  }, []);

  const loadMe = useCallback(async (retry = true) => {
    try {
      const { data } = await api.get("/auth/me");
      uidRef.current = data.user_id;
      setUser(data);
      await refreshOnboarding();
      setAuthError("");
    } catch (e) {
      if (retry && e?.response?.status === 401) {
        try {
          await api.post("/auth/refresh");
          return loadMe(false);
        } catch {
          /* not signed in */
        }
      }
      setUser(null);
      if (e?.response?.status !== 401) setAuthError(e?.response?.data?.detail || "We couldn't verify your sign-in. Please try again.");
    }
  }, [refreshOnboarding]);

  const applySession = useCallback(async (session) => {
    if (!session) {
      uidRef.current = null;
      setUser(null);
      setOnboarding(null);
      return;
    }
    if (uidRef.current === session.user.id) return;
    await loadMe();
  }, [loadMe]);

  useEffect(() => {
    let sub;
    (async () => {
      try {
        const { data: cfg } = await api.get("/config");
        setConfig(cfg);
        if (cfg.auth_mode === "emergent") {
          const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
          if (sid) {
            window.history.replaceState(null, "", window.location.pathname);
            try {
              await api.post("/auth/session", { session_id: sid });
            } catch (e) {
              setAuthError(e?.response?.data?.detail || "Sign-in failed. Please try again.");
            }
          }
          await loadMe();
        } else if (cfg.auth_configured) {
          const sb = initSupabase(cfg.supabase_url, cfg.supabase_anon_key);
          const { data } = await sb.auth.getSession();
          await applySession(data.session);
          sub = sb.auth.onAuthStateChange((event, session) => {
            if (["SIGNED_IN", "SIGNED_OUT", "USER_DELETED"].includes(event)) setTimeout(() => applySession(session), 0);
          }).data.subscription;
        }
      } catch {
        setConfig((c) => c || { auth_configured: false, unreachable: true });
      } finally {
        setLoading(false);
      }
    })();
    return () => sub?.unsubscribe();
  }, [applySession, loadMe]);

  const login = async (next = "/app") => {
    sessionStorage.setItem(NEXT_KEY, next);
    if (config?.auth_mode === "emergent") {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = `${window.location.origin}/auth/callback`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    // Only basic Google scopes (openid email profile) are requested.
    await getSupabase().auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback`, queryParams: { prompt: "select_account" } },
    });
  };

  const passwordAuth = async (mode, payload) => {
    if (config?.auth_mode === "supabase") {
      const sb = getSupabase();
      const res = mode === "register"
        ? await sb.auth.signUp({ email: payload.email, password: payload.password, options: { data: { full_name: payload.name }, emailRedirectTo: `${window.location.origin}/auth/callback` } })
        : await sb.auth.signInWithPassword({ email: payload.email, password: payload.password });
      if (res.error) throw new Error(res.error.message);
      if (!res.data.session) return "check_email";
      await loadMe();
      return "ok";
    }
    await api.post(mode === "register" ? "/auth/register" : "/auth/login", payload);
    await loadMe();
    return "ok";
  };

  const logout = async (scope = "global") => {
    try {
      if (config?.auth_mode === "emergent") await api.post("/auth/logout");
      else await getSupabase()?.auth.signOut({ scope });
    } catch {
      /* already signed out */
    }
    uidRef.current = null;
    setUser(null);
    setOnboarding(null);
  };

  return (
    <AuthContext.Provider value={{ config, passwordAuth, user, setUser, onboarding, setOnboarding, refreshOnboarding, loading, authError, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
